import openai from './openaiClient';
import { retryOpenAICall } from '../utils/retryWrapper';
import { withRateLimit, getRateLimitStatus } from '../utils/rateLimiter';

/**
 * AI Service for book authoring workflow
 * Provides GPT-4 powered content generation with rate limiting and retry logic
 */

/**
 * Estimate tokens for a request (rough approximation)
 */
function estimateTokens(text, maxTokens = 1000) {
  // Rough estimation: 1 token ≈ 4 characters
  const inputTokens = Math.ceil((text?.length || 0) / 4);
  return inputTokens + maxTokens;
}

/**
 * Generates book topic recommendations based on user's industry and goals
 * @param {Object} formData - User input data
 * @returns {Promise<string>} Topic recommendations
 */
export async function generateTopicRecommendations(formData) {
  try {
    const prompt = `You are an expert book topic researcher and Amazon BSR analyst. Based on the following information:
    
Industry/Niche: ${formData?.industry}
Target Audience: ${formData?.audience}
Book Goal: ${formData?.goal}

Analyze current market trends and generate 20 high-potential book topics that would:
1. Establish authority in the specified industry
2. Appeal to the target audience
3. Achieve the stated goal
4. Have strong commercial viability on Amazon

For each topic:
- Provide a compelling title
- Indicate market potential (high/medium search volume, competition level)
- Suggest why it would work for this author's goals

Format the output clearly with numbered topics and include a final recommendation for the best topic with reasoning.`;

    const estimatedTokens = estimateTokens(prompt, 3000);

    const response = await withRateLimit(
      async () => {
        return await retryOpenAICall(async () => {
          return await openai?.chat?.completions?.create({
            model: 'gpt-4-turbo',
            messages: [
              { 
                role: 'system', 
                content: 'You are an expert book strategist specializing in Amazon publishing, market research, and authority building through books. Provide actionable, data-driven recommendations.' 
              },
              { role: 'user', content: prompt },
            ],
            temperature: 0.7,
            max_tokens: 3000,
          });
        });
      },
      { estimatedTokens, queueIfLimited: true }
    );

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    if (error?.code === 'RATE_LIMIT_EXCEEDED') {
      const status = getRateLimitStatus();
      throw new Error(
        `Rate limit exceeded. Please wait ${error?.details?.waitSeconds || 0} seconds. ` +
        `Current usage: ${status?.usage?.minuteRequests}/${status?.limits?.requestsPerMinute} requests per minute.`
      );
    }
    console.error('Error generating topic recommendations:', error);
    throw new Error('Failed to generate topic recommendations. Please check your OpenAI API key and try again.');
  }
}

/**
 * Analyzes writing voice from user's uploaded content
 * @param {string} writingSample - Text sample from user
 * @returns {Promise<string>} Writing voice analysis
 */
export async function analyzeWritingVoice(writingSample) {
  try {
    const prompt = `Analyze the following writing sample and create a comprehensive writing voice profile:

${writingSample}

Provide a detailed analysis covering:
1. Tone (professional, conversational, authoritative, etc.)
2. Style (narrative, instructional, storytelling, etc.)
3. Vocabulary Level (grade level, complexity)
4. Sentence Structure (length, complexity, variety)
5. Key Phrases and expressions commonly used
6. Perspective (first-person, third-person, etc.)
7. Pacing and rhythm
8. Unique characteristics that make this voice distinctive

This profile will be used to maintain consistency in AI-generated book content.`;

    const estimatedTokens = estimateTokens(prompt, 2000);

    const response = await withRateLimit(
      async () => {
        return await retryOpenAICall(async () => {
          return await openai?.chat?.completions?.create({
            model: 'gpt-4-turbo',
            messages: [
              { 
                role: 'system', 
                content: 'You are an expert writing coach and voice analyst. Provide deep, actionable insights into writing style that can be used to replicate the voice consistently.' 
              },
              { role: 'user', content: prompt },
            ],
            temperature: 0.7,
            max_tokens: 2000,
          });
        });
      },
      { estimatedTokens, queueIfLimited: true }
    );

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    if (error?.code === 'RATE_LIMIT_EXCEEDED') {
      const status = getRateLimitStatus();
      throw new Error(
        `Rate limit exceeded. Please wait ${error?.details?.waitSeconds || 0} seconds. ` +
        `Current usage: ${status?.usage?.minuteRequests}/${status?.limits?.requestsPerMinute} requests per minute.`
      );
    }
    console.error('Error analyzing writing voice:', error);
    throw new Error('Failed to analyze writing voice. Please try again.');
  }
}

/**
 * Generates detailed book outline
 * @param {Object} formData - Book details
 * @returns {Promise<string>} Book outline
 */
export async function generateBookOutline(formData) {
  try {
    const prompt = `Create a detailed, professional book outline with the following specifications:

Book Title: ${formData?.title}
Target Word Count: ${formData?.wordCount} words
Number of Chapters: ${formData?.chapters}
Key Topics: ${formData?.topics || 'Not specified'}

Generate a comprehensive outline including:
1. Foreword (optional)
2. Introduction - Hook the reader and establish credibility
3. Main chapters - organized into logical sections/parts if appropriate
4. Conclusion - Tie everything together and inspire action
5. Resources/Tools appendix
6. About the Author section

For each chapter:
- Provide a compelling chapter title
- Include 3-5 key points or subtopics
- Suggest approximate word count
- Indicate the chapter's purpose and value to the reader Ensure the outline flows logically and builds towards the book's ultimate goal.`;

    const estimatedTokens = estimateTokens(prompt, 3000);

    const response = await withRateLimit(
      async () => {
        return await retryOpenAICall(async () => {
          return await openai?.chat?.completions?.create({
            model: 'gpt-4-turbo',
            messages: [
              { 
                role: 'system', 
                content: 'You are an expert book architect and content strategist. Create well-structured, engaging outlines that guide readers through a transformative journey.' 
              },
              { role: 'user', content: prompt },
            ],
            temperature: 0.7,
            max_tokens: 3000,
          });
        });
      },
      { estimatedTokens, queueIfLimited: true }
    );

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    if (error?.code === 'RATE_LIMIT_EXCEEDED') {
      const status = getRateLimitStatus();
      throw new Error(
        `Rate limit exceeded. Please wait ${error?.details?.waitSeconds || 0} seconds. ` +
        `Current usage: ${status?.usage?.minuteRequests}/${status?.limits?.requestsPerMinute} requests per minute.`
      );
    }
    console.error('Error generating book outline:', error);
    throw new Error('Failed to generate book outline. Please try again.');
  }
}

/**
 * Generates a single chapter with the author's writing voice
 * @param {Object} chapterInfo - Chapter details
 * @param {string} writingVoice - Author's writing voice profile
 * @param {string} bookContext - Overall book context and outline
 * @returns {Promise<string>} Generated chapter content
 */
export async function generateChapter(chapterInfo, writingVoice, bookContext) {
  try {
    const prompt = `Generate a complete chapter for a book with the following details:

Chapter Number: ${chapterInfo?.chapterNumber}
Chapter Title: ${chapterInfo?.title}
Target Word Count: ${chapterInfo?.wordCount || '4000-5000'} words
Key Points to Cover: ${chapterInfo?.keyPoints}

Book Context:
${bookContext}

Writing Voice Profile (MUST MAINTAIN THIS STYLE):
${writingVoice}

Requirements:
1. Match the writing voice exactly - tone, style, vocabulary, sentence structure
2. Include practical examples and actionable takeaways
3. Make it engaging and valuable to the reader
4. Use storytelling where appropriate
5. Include transitions from previous chapter context
6. End with a hook or transition to the next chapter
7. Reach the target word count

Write the complete chapter content now:`;

    const estimatedTokens = estimateTokens(prompt + writingVoice + bookContext, 8000);

    const response = await withRateLimit(
      async () => {
        return await retryOpenAICall(async () => {
          return await openai?.chat?.completions?.create({
            model: 'gpt-4-turbo',
            messages: [
              { 
                role: 'system', 
                content: 'You are an expert ghostwriter specializing in business and professional books. Write in the exact voice provided, maintaining consistency while creating engaging, valuable content.' 
              },
              { role: 'user', content: prompt },
            ],
            temperature: 0.7,
            max_tokens: 8000,
          });
        });
      },
      { estimatedTokens, queueIfLimited: true }
    );

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    if (error?.code === 'RATE_LIMIT_EXCEEDED') {
      const status = getRateLimitStatus();
      throw new Error(
        `Rate limit exceeded. Please wait ${error?.details?.waitSeconds || 0} seconds. ` +
        `Current usage: ${status?.usage?.minuteRequests}/${status?.limits?.requestsPerMinute} requests per minute.`
      );
    }
    console.error('Error generating chapter:', error);
    throw new Error('Failed to generate chapter content. Please try again.');
  }
}

/**
 * Generates complete manuscript (all chapters at once)
 * @param {Object} bookData - Complete book information
 * @returns {Promise<string>} Progress updates (for streaming)
 */
export async function generateFullManuscript(bookData, onProgress) {
  try {
    const chapters = [];
    const totalChapters = parseInt(bookData?.outline?.chapters || 10);

    for (let i = 1; i <= totalChapters; i++) {
      if (onProgress) {
        onProgress({
          chapter: i,
          total: totalChapters,
          status: 'generating',
          percentage: Math.round((i / totalChapters) * 100),
        });
      }

      const chapterContent = await generateChapter(
        {
          chapterNumber: i,
          title: `Chapter ${i}`,
          wordCount: Math.round(bookData?.totalWordCount / totalChapters),
          keyPoints: bookData?.outline?.chapters?.[i - 1]?.points || '',
        },
        bookData?.writingVoice,
        bookData?.outline?.summary || ''
      );

      chapters?.push(chapterContent);
    }

    return chapters?.join('\n\n---\n\n');
  } catch (error) {
    console.error('Error generating full manuscript:', error);
    throw new Error('Failed to generate manuscript. Please try again.');
  }
}

/**
 * Generates KDP metadata (categories, keywords, description)
 * @param {Object} bookData - Book information
 * @returns {Promise<string>} Optimized metadata
 */
export async function generateKDPMetadata(bookData) {
  try {
    const prompt = `Generate optimized Amazon KDP metadata for the following book:

Title: ${bookData?.title}
Subtitle: ${bookData?.subtitle || ''}
Book Description: ${bookData?.description}
Target Category: ${bookData?.category}
Provided Keywords: ${bookData?.keywords || ''}

Generate:
1. An optimized, compelling book description (150-200 words) that:
   - Hooks readers in the first sentence
   - Highlights key benefits and transformations
   - Uses emotional triggers
   - Includes a clear call-to-action
   - Is formatted for maximum readability

2. Three optimal BISAC categories with codes

3. Seven high-converting keywords optimized for Amazon's search algorithm:
   - Focus on buyer intent keywords
   - Include long-tail variations
   - Consider competition and search volume
   - Avoid overly competitive single words

4. Additional metadata suggestions:
   - Age range
   - Target audience description
   - Competitive positioning

Format the output clearly for easy copying into KDP.`;

    const estimatedTokens = estimateTokens(prompt, 2500);

    const response = await withRateLimit(
      async () => {
        return await retryOpenAICall(async () => {
          return await openai?.chat?.completions?.create({
            model: 'gpt-4-turbo',
            messages: [
              { 
                role: 'system', 
                content: 'You are an Amazon KDP optimization expert with deep knowledge of search algorithms, category selection, and metadata best practices for maximum visibility and sales.' 
              },
              { role: 'user', content: prompt },
            ],
            temperature: 0.7,
            max_tokens: 2500,
          });
        });
      },
      { estimatedTokens, queueIfLimited: true }
    );

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    if (error?.code === 'RATE_LIMIT_EXCEEDED') {
      const status = getRateLimitStatus();
      throw new Error(
        `Rate limit exceeded. Please wait ${error?.details?.waitSeconds || 0} seconds. ` +
        `Current usage: ${status?.usage?.minuteRequests}/${status?.limits?.requestsPerMinute} requests per minute.`
      );
    }
    console.error('Error generating KDP metadata:', error);
    throw new Error('Failed to generate KDP metadata. Please try again.');
  }
}

/**
 * Generates cover design concepts
 * @param {Object} coverSpecs - Cover specifications
 * @returns {Promise<string>} Cover design recommendations
 */
export async function generateCoverConcepts(coverSpecs) {
  try {
    const prompt = `Generate three professional book cover design concepts based on:

Book Title: ${coverSpecs?.title}
Subtitle: ${coverSpecs?.subtitle || ''}
Cover Style: ${coverSpecs?.coverStyle}
Color Scheme: ${coverSpecs?.colorScheme}
Include Author Photo: ${coverSpecs?.authorPhoto ? 'Yes' : 'No'}
Target Genre/Category: ${coverSpecs?.category || 'Business/Professional'}

For each concept, provide:
1. Detailed visual description (layout, typography, imagery)
2. Color palette with hex codes
3. Typography recommendations (font families, sizes, hierarchy)
4. Design rationale (why this would work for this book)
5. Competitive positioning (how it stands out in the category)

Also include:
- Back cover layout suggestions
- Spine design considerations
- Print vs. digital optimization tips
- File preparation guidelines for designers

Base recommendations on current bestseller trends in the category.`;

    const estimatedTokens = estimateTokens(prompt, 2500);

    const response = await withRateLimit(
      async () => {
        return await retryOpenAICall(async () => {
          return await openai?.chat?.completions?.create({
            model: 'gpt-4-turbo',
            messages: [
              { 
                role: 'system', 
                content: 'You are an expert book cover designer with deep knowledge of design trends, typography, and what sells in different book categories. Provide actionable design direction.' 
              },
              { role: 'user', content: prompt },
            ],
            temperature: 0.7,
            max_tokens: 2500,
          });
        });
      },
      { estimatedTokens, queueIfLimited: true }
    );

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    if (error?.code === 'RATE_LIMIT_EXCEEDED') {
      const status = getRateLimitStatus();
      throw new Error(
        `Rate limit exceeded. Please wait ${error?.details?.waitSeconds || 0} seconds. ` +
        `Current usage: ${status?.usage?.minuteRequests}/${status?.limits?.requestsPerMinute} requests per minute.`
      );
    }
    console.error('Error generating cover concepts:', error);
    throw new Error('Failed to generate cover concepts. Please try again.');
  }
}

/**
 * Generates publishing checklist and guidance
 * @returns {Promise<string>} Publishing checklist
 */
export async function generatePublishingGuidance() {
  try {
    const prompt = `Create a comprehensive, step-by-step publishing checklist for Amazon KDP that covers:

Pre-Publishing:
1. Manuscript finalization and proofread checklist
2. File format requirements (print and ebook)
3. ISBN and copyright considerations
4. Cover file specifications

KDP Setup:
1. Account creation walkthrough
2. Book details entry (title, description, keywords)
3. Pricing strategy recommendations
4. Rights and royalty selection
5. Print-on-demand specifications
6. Kindle format requirements

Launch Strategy:
1. Pre-launch checklist (proof copies, reviews, etc.)
2. Launch day optimization
3. Marketing timeline (first 30 days)
4. Community building recommendations

Post-Launch:
1. Performance monitoring
2. Review generation strategies
3. Advertising options (KDP Ads, Amazon Ads)
4. Long-term authority building

Include:
- Common mistakes to avoid
- Time estimates for each step
- Resource links and tools
- Tips for first-time authors

Format as a checklist with actionable items.`;

    const estimatedTokens = estimateTokens(prompt, 3000);

    const response = await withRateLimit(
      async () => {
        return await retryOpenAICall(async () => {
          return await openai?.chat?.completions?.create({
            model: 'gpt-4-turbo',
            messages: [
              { 
                role: 'system', 
                content: 'You are an experienced book publishing consultant specializing in Amazon KDP. Provide practical, proven guidance that leads to successful launches.' 
              },
              { role: 'user', content: prompt },
            ],
            temperature: 0.7,
            max_tokens: 3000,
          });
        });
      },
      { estimatedTokens, queueIfLimited: true }
    );

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    if (error?.code === 'RATE_LIMIT_EXCEEDED') {
      const status = getRateLimitStatus();
      throw new Error(
        `Rate limit exceeded. Please wait ${error?.details?.waitSeconds || 0} seconds. ` +
        `Current usage: ${status?.usage?.minuteRequests}/${status?.limits?.requestsPerMinute} requests per minute.`
      );
    }
    console.error('Error generating publishing guidance:', error);
    throw new Error('Failed to generate publishing guidance. Please try again.');
  }
}

/**
 * Refines/regenerates content based on user feedback
 * @param {string} content - Original content
 * @param {string} feedback - User's feedback
 * @param {string} writingVoice - Writing voice profile
 * @returns {Promise<string>} Refined content
 */
export async function refineContent(content, feedback, writingVoice) {
  try {
    const prompt = `Refine the following content based on user feedback while maintaining the writing voice:

ORIGINAL CONTENT:
${content}

USER FEEDBACK:
${feedback}

WRITING VOICE TO MAINTAIN:
${writingVoice}

Provide the refined version that:
1. Addresses all feedback points
2. Maintains the established writing voice
3. Improves clarity and impact
4. Keeps or increases the word count as appropriate

Output only the refined content, ready to use:`;

    const estimatedTokens = estimateTokens(prompt + content + feedback + writingVoice, 8000);

    const response = await withRateLimit(
      async () => {
        return await retryOpenAICall(async () => {
          return await openai?.chat?.completions?.create({
            model: 'gpt-4-turbo',
            messages: [
              { 
                role: 'system', 
                content: 'You are an expert editor and writing coach. Refine content based on feedback while maintaining stylistic consistency and improving quality.' 
              },
              { role: 'user', content: prompt },
            ],
            temperature: 0.7,
            max_tokens: 8000,
          });
        });
      },
      { estimatedTokens, queueIfLimited: true }
    );

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    if (error?.code === 'RATE_LIMIT_EXCEEDED') {
      const status = getRateLimitStatus();
      throw new Error(
        `Rate limit exceeded. Please wait ${error?.details?.waitSeconds || 0} seconds. ` +
        `Current usage: ${status?.usage?.minuteRequests}/${status?.limits?.requestsPerMinute} requests per minute.`
      );
    }
    console.error('Error refining content:', error);
    throw new Error('Failed to refine content. Please try again.');
  }
}

/**
 * Get current rate limit status
 * @returns {Object} Rate limit status with usage and remaining capacity
 */
export function getAIServiceRateLimitStatus() {
  return getRateLimitStatus();
}

export default {
  generateTopicRecommendations,
  analyzeWritingVoice,
  generateBookOutline,
  generateChapter,
  generateFullManuscript,
  generateKDPMetadata,
  generateCoverConcepts,
  generatePublishingGuidance,
  refineContent,
  getAIServiceRateLimitStatus,
};